import React, { useState, useRef } from 'react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CompanyInformation = ({ businessProfile, onSave, saving }) => {
  const [formData, setFormData] = useState({
    name: businessProfile?.name || '',
    description: businessProfile?.description || '',
    category: businessProfile?.category || '',
    website_url: businessProfile?.website_url || '',
    contact_email: businessProfile?.contact_email || '',
    contact_phone: businessProfile?.contact_phone || '',
    address: businessProfile?.address || '',
    city: businessProfile?.city || '',
    country: businessProfile?.country || '',
    founded_year: businessProfile?.founded_year || '',
    employee_count_range: businessProfile?.employee_count_range || '',
    logo_url: businessProfile?.logo_url || ''
  });
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef();

  const businessCategories = [
    { value: 'technology', label: 'Technology' },
    { value: 'transportation', label: 'Transportation' },
    { value: 'energy', label: 'Energy' },
    { value: 'water_management', label: 'Water Management' },
    { value: 'waste_management', label: 'Waste Management' },
    { value: 'governance', label: 'Governance' },
    { value: 'education', label: 'Education' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'safety_security', label: 'Safety & Security' },
    { value: 'economic_development', label: 'Economic Development' }
  ];

  const employeeRanges = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-500', label: '201-500 employees' },
    { value: '501-1000', label: '501-1000 employees' },
    { value: '1000+', label: '1000+ employees' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (!file?.type?.startsWith('image/')) {
      setErrors(prev => ({ ...prev, logo: 'Please select an image file' }));
      return;
    }

    if (file?.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, logo: 'File size must be less than 5MB' }));
      return;
    }

    try {
      setUploading(true);
      setErrors(prev => ({ ...prev, logo: null }));

      const fileExt = file?.name?.split('.')?.pop();
      const fileName = `${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}.${fileExt}`;
      const filePath = `business-logos/${fileName}`;

      const { error: uploadError } = await supabase?.storage
        ?.from('business-media')
        ?.upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase?.storage
        ?.from('business-media')
        ?.getPublicUrl(filePath);

      setFormData(prev => ({ ...prev, logo_url: publicUrl }));
    } catch (error) {
      console.error('Error uploading logo:', error);
      setErrors(prev => ({ ...prev, logo: 'Failed to upload logo. Please try again.' }));
    } finally {
      setUploading(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) newErrors.name = 'Company name is required';
    if (!formData?.category) newErrors.category = 'Category is required';
    if (!formData?.description?.trim()) newErrors.description = 'Description is required';
    if (formData?.website_url && !isValidUrl(formData?.website_url)) {
      newErrors.website_url = 'Please enter a valid URL';
    }
    if (formData?.contact_email && !isValidEmail(formData?.contact_email)) {
      newErrors.contact_email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;

    const result = await onSave?.('company', formData);
    if (result?.success) {
      // Success feedback could be added here
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Company Information</h3>
        <p className="text-sm text-gray-600 mb-6">
          Provide comprehensive details about your company to build trust and credibility with potential partners.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Basic Information</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input
                label="Company Name *"
                name="name"
                value={formData?.name}
                onChange={handleInputChange}
                error={errors?.name}
                placeholder="Enter your company name"
              />
            </div>

            <div>
              <Select
                label="Business Category *"
                name="category"
                value={formData?.category}
                onChange={handleInputChange}
                options={businessCategories}
                error={errors?.category}
                placeholder="Select your primary category"
              />
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Description *
              </label>
              <textarea
                name="description"
                value={formData?.description}
                onChange={handleInputChange}
                rows={4}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                  errors?.description ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Describe your company's mission, vision, and what you do..."
              />
              {errors?.description && (
                <p className="mt-1 text-sm text-red-600">{errors?.description}</p>
              )}
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Contact Information</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Input
                label="Website URL"
                name="website_url"
                type="url"
                value={formData?.website_url}
                onChange={handleInputChange}
                error={errors?.website_url}
                placeholder="https://yourcompany.com"
              />
            </div>

            <div>
              <Input
                label="Contact Email"
                name="contact_email"
                type="email"
                value={formData?.contact_email}
                onChange={handleInputChange}
                error={errors?.contact_email}
                placeholder="contact@yourcompany.com"
              />
            </div>

            <div>
              <Input
                label="Contact Phone"
                name="contact_phone"
                type="tel"
                value={formData?.contact_phone}
                onChange={handleInputChange}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div>
              <Input
                label="Founded Year"
                name="founded_year"
                type="number"
                min="1800"
                max={new Date()?.getFullYear()}
                value={formData?.founded_year}
                onChange={handleInputChange}
                placeholder="2020"
              />
            </div>
          </div>
        </div>

        {/* Location Information */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Location</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <Input
                label="Address"
                name="address"
                value={formData?.address}
                onChange={handleInputChange}
                placeholder="Street address"
              />
            </div>

            <div>
              <Input
                label="City"
                name="city"
                value={formData?.city}
                onChange={handleInputChange}
                placeholder="City"
              />
            </div>

            <div>
              <Input
                label="Country"
                name="country"
                value={formData?.country}
                onChange={handleInputChange}
                placeholder="Country"
              />
            </div>
          </div>
        </div>

        {/* Company Details */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">Company Details</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Select
                label="Employee Count"
                name="employee_count_range"
                value={formData?.employee_count_range}
                onChange={handleInputChange}
                options={employeeRanges}
                placeholder="Select employee count range"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company Logo
              </label>
              <div className="flex items-center space-x-4">
                {formData?.logo_url && (
                  <img
                    src={formData?.logo_url}
                    alt="Company logo"
                    className="w-16 h-16 rounded-lg object-cover border"
                  />
                )}
                <div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef?.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? 'Uploading...' : formData?.logo_url ? 'Change Logo' : 'Upload Logo'}
                  </Button>
                  {errors?.logo && (
                    <p className="mt-1 text-sm text-red-600">{errors?.logo}</p>
                  )}
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Recommended: Square image, max 5MB, JPG/PNG format
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end pt-6 border-t">
          <Button
            type="submit"
            disabled={saving || uploading}
            className="px-8"
          >
            {saving ? 'Saving...' : 'Save Company Information'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CompanyInformation;